import type { SetupStepId } from '@shared/setup'
import { enableBiometricUnlock } from '../../../services/biometric-auth-service'
import {
  openPermissionSettings,
  requestAndroidPreciseLocation,
  requestPermission,
  type PermissionKind,
  type PermissionStatus,
} from '../../../services/permissions/permissions-service'
import {
  openFullScreenIntentSettings,
  openIncomingCallsChannelSettings,
  requestIgnoreBatteryOptimizations,
} from '../../../services/voice-call/native-call'
import { getNativePlatform } from '../../../utils/platform'
import { withTimeout } from './setup-probes'

/** The user has this long to read and answer an OS dialog. */
export const REQUEST_TIMEOUT_MS = 20_000

const PERMISSION_STEPS: Partial<Record<SetupStepId, PermissionKind>> = {
  notifications: 'notifications',
  microphone: 'microphone',
  camera: 'camera',
  location: 'location',
}

/** What the OS answered to an "Allow" tap, as the setup flow needs to react to it. */
export function permissionOutcome(status: PermissionStatus): StepActionResult {
  if (status === 'denied') return 'denied'
  if (status === 'prompt') return 'no-answer'
  return 'answered'
}

export type StepActionResult =
  /** The OS answered (re-probe to read the new state). */
  | 'answered'
  /** The OS said no and will not ask again: only phone Settings can turn it on. */
  | 'denied'
  /** No yes and no hard no (dialog dismissed, never shown, or the request failed). */
  | 'no-answer'
  /** The phone has not answered yet (its question may still be on screen). */
  | 'timed-out'
  /** We sent the person to Settings; the state changes when they come back. */
  | 'opened-settings'
  /** Settings could not be opened from here (web, older build). */
  | 'no-settings'

/**
 * The primary button of a device step. MUST run from a user gesture: it is
 * the only place setup fires an OS dialog or deep-links to Settings.
 */
export async function runStepAction(
  id: SetupStepId,
  opts: { blocked: boolean; identity: string; biometricLabel: string; attempt: number },
): Promise<StepActionResult> {
  const permission = PERMISSION_STEPS[id]
  if (permission) {
    if (opts.blocked) return (await openPermissionSettings()) ? 'opened-settings' : 'no-settings'
    const status = await withTimeout<PermissionStatus | 'timeout'>(
      // A failed request counts as "no answer", not as a reason to wait.
      requestPermission(permission).catch((): PermissionStatus => 'prompt'),
      'timeout',
      REQUEST_TIMEOUT_MS,
    )
    return status === 'timeout' ? 'timed-out' : permissionOutcome(status)
  }
  switch (id) {
    case 'lock-screen-calls':
      await openFullScreenIntentSettings()
      return 'opened-settings'
    case 'calls-channel':
      return (await openIncomingCallsChannelSettings()) ? 'opened-settings' : 'no-settings'
    case 'battery':
      // A system dialog that can always be shown again.
      await withTimeout(requestIgnoreBatteryOptimizations(), undefined, REQUEST_TIMEOUT_MS)
      return 'answered'
    case 'location-precise':
      // Android first offers its own "change to precise" dialog; after that, Settings.
      if (getNativePlatform() === 'android' && opts.attempt === 0) {
        await withTimeout(requestAndroidPreciseLocation(), 'unknown' as const, REQUEST_TIMEOUT_MS)
        return 'answered'
      }
      return (await openPermissionSettings()) ? 'opened-settings' : 'no-settings'
    case 'app-lock': {
      const result = await enableBiometricUnlock(opts.identity, { label: opts.biometricLabel })
      if (!result.ok && !result.cancelled) throw new Error(result.message)
      return 'answered'
    }
    default:
      return 'answered'
  }
}
